import type { components } from '@joyful/openapi/types.gen'
import { serializeEncounterNavigationSource } from '../../encounters/hooks/useEncounterNavigationSource'
import { createEnumSchema, UserRoleSchema } from '@joyful/utils'
export type ListWorkBlocksResponse =
  components['schemas']['ListWorkBlocksResponse']
export type WorkBlocksMetrics = components['schemas']['WorkBlocksMetrics']
export type WorkBlock = components['schemas']['WorkBlockWithEncounters']
export type WorkBlockAssignment =
  components['schemas']['WorkBlockAssignmentWithEncounter']
export type WorkBlockAssignmentGroup = NonNullable<
  WorkBlock['assignment_groups']
>[number]
export type EncounterListItem = components['schemas']['EncounterListItem']
export type Biller = components['schemas']['UserRead']
export type UnassignedBiller = components['schemas']['UnassignedBiller']

export interface WorkBlockAssignmentStats {
  completedAssignments: number
  totalAssignments: number
}

export function isReserveWorkBlock(block: WorkBlock): boolean {
  return block.kind === 'RESERVE'
}

export function collectBlockEncounters(
  block: WorkBlock,
): Array<EncounterListItem> {
  return block.assignments.map((assignment) => {
    return assignment.encounter
  })
}

export interface WorkBlockEncounters {
  allEncounters: Array<EncounterListItem>
  assignmentsByEncounterId: Map<string, WorkBlockAssignment>
  completedEncounterIds: Set<string>
  completedEncounters: Array<EncounterListItem>
  todoEncounters: Array<EncounterListItem>
}

export function collectOrderedBlockEncounters(
  block: WorkBlock,
): WorkBlockEncounters {
  return collectOrderedAssignmentEncounters(block.assignments)
}

export function collectOrderedAssignmentEncounters(
  assignments: Array<WorkBlockAssignment>,
): WorkBlockEncounters {
  const assignmentsByEncounterId = new Map<string, WorkBlockAssignment>()
  const completedEncounterIds = new Set<string>()
  const todoEncounters = new Array<components['schemas']['EncounterListItem']>()
  const completedAssignments = new Array<
    components['schemas']['WorkBlockAssignmentWithEncounter']
  >()

  for (const assignment of assignments) {
    assignmentsByEncounterId.set(assignment.encounter_id, assignment)
    if (assignment.completed_at) {
      completedEncounterIds.add(assignment.encounter_id)
      completedAssignments.push(assignment)
    } else {
      todoEncounters.push(assignment.encounter)
    }
  }

  completedAssignments.sort((a, b) => {
    const aCompletedAt = a.completed_at ?? ''
    const bCompletedAt = b.completed_at ?? ''
    return bCompletedAt.localeCompare(aCompletedAt)
  })

  const completedEncounters = completedAssignments.map((assignment) => {
    return assignment.encounter
  })

  return {
    allEncounters: todoEncounters.concat(completedEncounters),
    assignmentsByEncounterId,
    completedEncounterIds,
    completedEncounters,
    todoEncounters,
  }
}

export function getAssignmentStats(
  block: WorkBlock | null | undefined,
): WorkBlockAssignmentStats {
  if (!block) {
    return {
      completedAssignments: 0,
      totalAssignments: 0,
    }
  }

  return {
    completedAssignments: block.completed_assignment_count,
    totalAssignments: block.assignment_count,
  }
}

export const WorkBlockAssigneeRoleSchema = createEnumSchema([
  {
    title: UserRoleSchema.meta('BILLER').title,
    value: 'BILLER',
  },
  {
    title: UserRoleSchema.meta('BILLING_SUPERVISOR').title,
    value: 'BILLING_SUPERVISOR',
  },
  {
    title: UserRoleSchema.meta('RCM_SPECIALIST').title,
    value: 'RCM_SPECIALIST',
  },
  {
    title: UserRoleSchema.meta('BILLING_MANAGER').title,
    value: 'BILLING_MANAGER',
  },
])

export const WorkBlockAssignmentCompletionSchema = createEnumSchema([
  {
    description: '0 assigned',
    title: 'Unassigned',
    value: 'unassigned',
  },
  {
    description: '> 0 assigned but 0 worked',
    title: 'Not started',
    value: 'not_started',
  },
  {
    description: '> 0 worked but not complete',
    title: 'In progress',
    value: 'in_progress',
  },
  {
    description: 'Worked ≥ assigned',
    title: 'Complete',
    value: 'complete',
  },
])

export function getWorkBlockAssignmentCompletion(
  stats: WorkBlockAssignmentStats,
) {
  if (stats.totalAssignments === 0) {
    return 'unassigned'
  }

  if (stats.completedAssignments === 0) {
    return 'not_started'
  }

  if (stats.completedAssignments >= stats.totalAssignments) {
    return 'complete'
  }

  return 'in_progress'
}

export const BillerAssignmentStatusSchema = createEnumSchema<
  components['schemas']['BillerAssignmentStatus']
>([
  { title: `On`, value: 'ON' },
  { title: `Paused`, value: 'PAUSED' },
  { title: `Off`, value: 'OFF' },
])

export interface BuildBlockEncounterUrlParams {
  blockBillerUserId: string | null | undefined
  currentUserId: string
  encounterId: string
  orgSlug: string
}

export function buildBlockEncounterUrl({
  blockBillerUserId,
  currentUserId,
  encounterId,
  orgSlug,
}: BuildBlockEncounterUrlParams) {
  if (!blockBillerUserId) {
    return `/${orgSlug}/${encounterId}`
  }

  const isOwnBlock = blockBillerUserId === currentUserId
  return serializeEncounterNavigationSource(
    `/${orgSlug}/${encounterId}`,
    isOwnBlock
      ? { source: 'block' }
      : { entityId: blockBillerUserId, source: 'block' },
  )
}
